import { SiteUrls } from '@tableau/shared-urls';
import { FlyoutListMenuWidget, MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import { BodyCell } from '@tableau/widgets-datagrid';
import React, { useRef } from 'react';
import * as TypeStyle from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { Icons } from '~src/commonInfra/components/Icons';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { TypeUnsafeTranslation } from '~src/commonInfra/services/IReactTranslateService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import {
  RevisionHistoryDialogBodyState,
  RevisionHistoryDialogBodyStateAndProps
} from '~src/revisionHistory/components/RevisionHistoryDialogBody';
import { RevisionHistoryActionButton, RevisionHistoryActionContext } from '~src/revisionHistory/interfaces/RevisionHistoryActionContext';
import { IContentRevision } from '~src/revisionHistory/serverApi/types/IContentRevision';
import { RevisionActionDatasource } from '~src/revisionHistoryDatasource/services/RevisionActionDatasource';
import { RevisionActionFlow } from '~src/revisionHistoryFlow/services/RevisionActionFlow';
import { RevisionActionPublishedConnection } from '~src/revisionHistoryPublishedConnection/services/RevisionActionPublishedConnection';
import { RevisionActionWorkbook } from '~src/revisionHistoryWorkbook/services/RevisionActionWorkbook';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import {
  DatasourceContentType,
  FlowContentType,
  IContentType,
  PublishedConnectionContentType,
  WorkbookContentType
} from '~src/spacesPlatform/models/ContentTypes';

export const REVISION_ACTION_CELL_BUTTON_WIDGET_ID='revision-action-cell-button-widget-id';
export const REVISION_ACTION_CELL_FLYOUT_MENU_ID='revision-action-cell-generic-flyout-menu-id';

export const REVISION_NAME_SPAN_ID = 'revision-name-span-id';
export const REVISION_NAME_ANCHOR_ID = 'revision-name-anchor-id';
export const REVISION_NAME_DIV_ID = 'revision-name-div-id';
export const REVISION_PUBLISH_TIME_SPAN_ID = 'revision-publish-time-span-id';

const revisionHistoryActionButtonStyleName = TypeStyle.style({
  border: 'none',
  verticalAlign: 'middle',
  $nest: {
    '&&': { height: 20, width: 20, minHeight: 20, minWidth: 20, padding: 0 },
    '&&::after': { display: 'none' }, // hides the dropdown arrow
  }
});

function getMenuActions(contentType: IContentType): RevisionHistoryActionButton<IContentRevision>[]{
  let menuActions: RevisionHistoryActionButton<IContentRevision>[];
  switch (contentType.name) {
    case WorkbookContentType.name:
      menuActions = RevisionActionWorkbook.getWorkbookRevisionActionButtons();
      break;
    case DatasourceContentType.name:
      menuActions = RevisionActionDatasource.getDatasourceRevisionActionButtons();
      break;
    case FlowContentType.name:
      menuActions = RevisionActionFlow.getFlowRevisionActionButtons();
      break;
    case PublishedConnectionContentType.name:
      menuActions = RevisionActionPublishedConnection.getPublishedConnectionRevisionActionButtons();
      break;
    default:
      throw new Error(Messages.revisionHistoryUnsupportedContentType());
  }
  return menuActions;
}

export type RevisionHistoryActionCellFormatterProps = BodyCell.Props & Pick<RevisionHistoryDialogBodyStateAndProps,
                                                                              'updateDialogState' | 'contentType'>;

export function RevisionHistoryActionCellFormatter(props: RevisionHistoryActionCellFormatterProps) {
    const menuActions = getMenuActions(props.contentType);
    const menuItems: MenuItemInfo[] = menuActions.map((action, index) => ({
        displayValue: TypeUnsafeTranslation.translate(action.translate),
        value: `${index}`,
    }));
    const revision = props.row.data as IContentRevision;
    const divStyle = {display: revision.deleted || revision.current ? 'none' : 'initial'};

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>();
    const handleMenuSelect = React.useCallback(
      (val) => {
        const context: RevisionHistoryActionContext<IContentRevision> = {
          selectedItem: revision,
          updateState: () =>
            props.updateDialogState(
              (prevState: RevisionHistoryDialogBodyState) => {
                return {
                  ...prevState,
                  deletedItem: prevState.selectedItem,
                  selectedItem: null,
                } as RevisionHistoryDialogBodyState;
              }
            ),
        };
        menuActions[val as number].execute(context);
        setIsOpen(false);
      },
      // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [revision]
    );
    const handleMenuClose = React.useCallback(() => setIsOpen(false), []);
    const handleClick = () => setIsOpen(!isOpen);

    return <div style={divStyle}>
      <MenuButtonWidget
        aria-controls={REVISION_ACTION_CELL_FLYOUT_MENU_ID}
        aria-label={Messages.moreActions()}
        className={revisionHistoryActionButtonStyleName}
        onClick={handleClick}
        ref={buttonRef}
        testId={REVISION_ACTION_CELL_BUTTON_WIDGET_ID}
      >
        <Icons.actionIcon />
      </MenuButtonWidget>
      {isOpen &&
        <FlyoutListMenuWidget
          allowAutoPosition
          anchorEl={buttonRef.current}
          id={REVISION_ACTION_CELL_FLYOUT_MENU_ID}
          menuItemInfos={menuItems}
          menuStyle={{ minWidth: 144 }}
          onSelect={handleMenuSelect}
          requestCloseAll={handleMenuClose}
          testId={REVISION_ACTION_CELL_FLYOUT_MENU_ID}
          toggleEl={buttonRef.current}
        />
      }
    </div>;
}

export interface RevisionNameFormatterProps {
  bodyCellProps: BodyCell.Props;
  contentType: IContentType;
  onDeletedRevision: (rowKey: string) => void;
  onCurrentRevision: (rowKey: string) => void;
}

export function RevisionNameFormatter(props: RevisionNameFormatterProps) {
  const revision = props.bodyCellProps.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);

  const revisionName = Messages.revisionHistoryRevisionNumberLabel({REVISION_NUMBER: props.bodyCellProps.value});
  let revisionNameElement: JSX.Element = <span id={REVISION_NAME_SPAN_ID}>{revisionName}</span>;
  if (props.contentType.name === WorkbookContentType.name && !revision.deleted && !revision.current) {
    // the name element links to Preview
    const path = new SiteUrls({
      siteUrlName: getSessionManagementServiceInstance().sessionSiteUrlName(),
      queryString: window.location.search,
    }).workbookRevisionPreviewUrl(revision.revisionPath);
    const anchorProps = {id: REVISION_NAME_ANCHOR_ID, href: path, target: '_blank'};
    revisionNameElement = <a {...anchorProps}>{revisionName}</a>;
  }

  let revisionStateElement: React.ReactElement<React.HTMLAttributes<HTMLElement>> = null;
  if (revision.deleted) {
    revisionStateElement = <span>{' (' + Messages.revisionDeletedLabel() + ')'}</span>;
    props.onDeletedRevision(props.bodyCellProps.row.key);
  } else if (revision.current) {
    revisionStateElement = <span style={{color: CommonStyles.Colors.NormalTextColor}}>{' (' + Messages.revisionCurrentLabel() + ')'}</span>;
    props.onCurrentRevision(props.bodyCellProps.row.key);
  }

  return revisionStateElement
    ? <div style={cellStyle} id={REVISION_NAME_DIV_ID}>{revisionNameElement}{revisionStateElement}</div>
    : revisionNameElement;
}

export function PublisherNameFormatter(props: BodyCell.Props) {
  const revision = props.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);
  return props.row.data.publisher
    ? <span style={cellStyle}>{props.row.data.publisher}</span>
    : <span className={'tb-hidden-user'} style={cellStyle}>{Messages.hiddenUser()}</span>;
}

function getCellStyle(revision: IContentRevision) {
  let cellStyle: React.CSSProperties = {};
  if (revision.deleted) {
    cellStyle = {color: CommonStyles.Colors.DisabledTextColor};
  }
  return cellStyle;
}

export function PublishTimeFormatter(props: BodyCell.Props) {
  const dateTime = props.row.data['publishTime'];
  const text = dateTime ? L10n.localizedDateTime(dateTime) : '';
  const revision = props.row.data as IContentRevision;
  let cellStyle = getCellStyle(revision);
  return <span data-tb-test-id={'publish-time-span-id'} id={REVISION_PUBLISH_TIME_SPAN_ID} style={cellStyle}>{text}</span>;
}
